import { createAction } from '@reduxjs/toolkit';
import type { Set as ImmutableSet } from 'immutable';

import type { accountsTypes } from 'app-components/flux/accounts';
import { errorPayloadCreator, getPayloadCreator, type Meta } from 'app-components/flux/core/actionHelpers';
import type { BaseResource, QcsSyncResourcesPayload } from 'app-components/flux/core/resourceStoreTypes';

import type { A2ATransferPayload, TransactionProps, TransactionRecord } from './transactionsTypes';

type GetTransactionsMeta = Meta<BaseResource, unknown, { dateOnAfter?: string | null }>;
/** `incremental`: the fetch sent modifiedAfter, so it only returns rows changed since the last sync. */
type TransactionSyncProps = { goalAccountIds?: ImmutableSet<string>; incremental?: boolean };
type TransactionSyncMeta =
  | Meta<BaseResource, unknown, TransactionSyncProps>
  | Meta<TransactionProps, unknown, TransactionSyncProps>;

/** Context of the fetch that undoes the optimistic update of a failed or queued transaction write. */
export const RECONCILE_WRITE_CONTEXT = 'RECONCILE_TRANSACTION_WRITE';

const syncTransactionPayloadCreator = getPayloadCreator<
  QcsSyncResourcesPayload<TransactionProps>,
  TransactionProps,
  TransactionSyncMeta
>();
const syncSingleTransactionPayloadCreator = getPayloadCreator<
  QcsSyncResourcesPayload<TransactionProps>,
  TransactionProps
>();
const transactionPayloadCreator = getPayloadCreator<TransactionRecord>();
const transactionsPayloadCreator = getPayloadCreator<
  TransactionRecord[],
  TransactionRecord,
  Meta<TransactionRecord> & { skipOptimistic?: boolean }
>();

export const getTransactions = createAction(
  'GET_TRANSACTIONS',
  getPayloadCreator<void, BaseResource, GetTransactionsMeta>(),
);
export const getTransactionsSuccess = createAction('GET_TRANSACTIONS_SUCCESS', syncTransactionPayloadCreator);
export const getTransactionsFailure = createAction('GET_TRANSACTIONS_FAILURE', errorPayloadCreator);
export const postRefreshTransactions = createAction('POST_REFRESH_TRANSACTIONS', getPayloadCreator());

export const getTransaction = createAction('GET_TRANSACTION', getPayloadCreator<{ id: string }>());
export const getTransactionSuccess = createAction('GET_TRANSACTION_SUCCESS', syncSingleTransactionPayloadCreator);
export const getTransactionFailure = createAction('GET_TRANSACTION_FAILURE', errorPayloadCreator);

export const createTransaction = createAction('CREATE_TRANSACTION', transactionPayloadCreator);
export const createTransactionSuccess = createAction('CREATE_TRANSACTION_SUCCESS', transactionPayloadCreator);
export const createTransactionFailure = createAction('CREATE_TRANSACTION_FAILURE', errorPayloadCreator);

export const updateTransaction = createAction('UPDATE_TRANSACTION', transactionPayloadCreator);
export const updateTransactionSuccess = createAction('UPDATE_TRANSACTION_SUCCESS', transactionPayloadCreator);
export const updateTransactionFailure = createAction('UPDATE_TRANSACTION_FAILURE', errorPayloadCreator);

export const deleteTransaction = createAction('DELETE_TRANSACTION', transactionPayloadCreator);
export const deleteTransactionSuccess = createAction('DELETE_TRANSACTION_SUCCESS', transactionPayloadCreator);
export const deleteTransactionFailure = createAction('DELETE_TRANSACTION_FAILURE', errorPayloadCreator);

export const batchTransactions = createAction('BATCH_TRANSACTIONS', transactionsPayloadCreator);
export const batchTransactionsSuccess = createAction('BATCH_TRANSACTIONS_SUCCESS', transactionsPayloadCreator);
export const batchTransactionsFailure = createAction('BATCH_TRANSACTIONS_FAILURE', errorPayloadCreator);

export const getGoalTransactions = createAction('GET_GOAL_TRANSACTIONS', getPayloadCreator());
export const getGoalTransactionsForGoal = createAction('GET_GOAL_TRANSACTIONS_FOR_GOAL', getPayloadCreator<string>());
export const getGoalTransactionsSuccess = createAction('GET_GOAL_TRANSACTIONS_SUCCESS', syncTransactionPayloadCreator);
export const getGoalTransactionsFailure = createAction('GET_GOAL_TRANSACTIONS_FAILURE', errorPayloadCreator);

export const importAppleWalletTransactions = createAction(
  'IMPORT_APPLE_WALLET_TRANSACTIONS',
  transactionsPayloadCreator,
);

export const applyTransactionsChanges = createAction('APPLY_TRANSACTIONS_CHANGES', transactionsPayloadCreator);

// following actions are just local clones of original actions to break circular dependencies
// TODO: We may be able to import the accounts actions from the flux/accounts/accountsActions file directly instead of from the index to avoid circular dependencies as well
export const applyAccountsChangesClone = createAction(
  'APPLY_ACCOUNTS_CHANGES',
  getPayloadCreator<accountsTypes.Account[]>(),
);

// ACTION API
type TransactionAction = 'match' | 'unmatch' | 'link-to-scheduled-txn' | 'unlink-to-scheduled-txn' | 'skip' | 'accept';
export type TransactionActionPayload = { action: TransactionAction; id: string; otherId?: string };

export const performTransactionAction = createAction(
  'PERFORM_TRANSACTION_ACTION',
  getPayloadCreator<TransactionActionPayload>(),
);
export const performTransactionActionSuccess = createAction(
  'PERFORM_TRANSACTION_ACTION_SUCCESS',
  getPayloadCreator<TransactionRecord | TransactionRecord[]>(),
);
export const performTransactionActionFailure = createAction('PERFORM_TRANSACTION_ACTION_FAILURE', errorPayloadCreator);

export const getTransactionsEarliestDateOn = createAction('GET_TRANSACTIONS_EARLIEST_DATE_ON', getPayloadCreator());
export const getTransactionsEarliestDateOnSucess = createAction(
  'GET_TRANSACTIONS_EARLIEST_DATE_ON_SUCCESS',
  getPayloadCreator<{ dateOn?: string }>(),
);
export const getTransactionsEarliestDateOnFailure = createAction(
  'GET_TRANSACTIONS_EARLIEST_DATE_ON_FAILURE',
  errorPayloadCreator,
);

export const initiateA2ATransfer = createAction('INITIATE_A2A_TRANSFER', getPayloadCreator<A2ATransferPayload>());
export const initiateA2ATransferSuccess = createAction('INITIATE_A2A_TRANSFER_SUCCESS', transactionsPayloadCreator);
export const initiateA2ATransferFailure = createAction('INITIATE_A2A_TRANSFER_FAILURE', errorPayloadCreator);
